// assets/wc2-loading-fixed.js — Sub-project E rewrite, CORRECTED (third selector-mismatch hotfix).
// Loading view controller. ONE setInterval, ONE job: update title/subtitle text + bar width.
//
// PREVIOUS BUG: the original wc-loading.js (and wc2-loading.js renamed copy) looked for
//   [data-wc-loading-copy]   ← DOES NOT EXIST in snippets/wolden-customize-modal.liquid
//   [data-wc-loading-bar]    ← DOES NOT EXIST
// The Liquid template actually exposes these data-wc-element attributes:
//   [data-wc-element="loading-title"]      ← h2  (e.g. "Starting art generation")
//   [data-wc-element="loading-subtitle"]   ← p   (e.g. "Spinning up the studio. ...")
//   [data-wc-element="progress-fill"]      ← div (bar fill — width % drives the visual)
// The ticker fired but wrote to null elements, so the modal showed the Liquid's static
// initial text forever and the progress bar never advanced. Symptom: modal opens, sits
// on "Starting art generation" / "Spinning up the studio." for the entire generation.
//
// CORRECTED behavior: lift the monolith's LOADING_TIMELINE (9 entries, title + subtitle
// pairs) and write into the actual DOM elements the Liquid renders.

import { qs } from './wc2-dom.js';
import { setModalState } from './wc2-modal.js';

// 9 phases, each [seconds-into-load, title, subtitle].
// Lifted verbatim from monolith lines 168-178.
const LOADING_TIMELINE = [
  [0,   'Starting art generation', 'Spinning up the studio. Most pieces take about a minute.'],
  [5,   'Analyzing your pet',      "Reading your pet's unique features. Those adorable ears won't be missed."],
  [13,  'Lifting your pet from the background', "Making way for your pet's grand entrance onto the scene."],
  [22,  'Painting the scene',      'Placing your pet into the artwork, adjusting light and perspective.'],
  [34,  'Adding finishing touches','Detail work - fur texture, expression, those cute paws.'],
  [50,  'Final polishes',          'Quality takes a moment. Almost there.'],
  [70,  'Almost ready',            'Everything is taking final shape'],
  [90,  'Finalizing',              "We're nearly there. Thanks for hanging on."],
  [120, 'Well this is embarrassing','Hurrying along as fast as we can, it does not normally take this long!'],
  [150, 'Still working on your art',"Taking longer than usual — we haven't lost it. Hang tight a little more."],
  [300, 'Still at it',             'Your art is still being finished. Busy days can take a few minutes.'],
];

// 2026-05-27 — Operator-spec'd two-segment progress curve + hard client deadline.
//   Phase 1 (fast):  0-50s  → fills 0% to 75%   (1.5%/sec)
//   Phase 2 (slow):  50-90s → fills 75% to 99%  (0.6%/sec)
//   Phase 3 (park):  90s+   → holds at 99% (never implies "done" until result lands)
// 2026-06-10 — DEADLINE NO LONGER DEAD-ENDS. Real generations routinely run
// past 150s (live p90 on the continue phases ≈ 305s) and the gen usually
// completes server-side even when the client's long-held HTTP response dies,
// so flipping straight to the error template threw away sellable art
// (PostHog mid-May→Jun: 925 generation_started vs 800 phase1_succeeded).
// Now: at GENERATION_DEADLINE_SEC the ticker fires 'wolden:gen-deadline'
// (wc77-upload.js answers it by polling fetch-gen) and keeps ticking with
// patient copy. The error flip only happens at HARD_FAIL_SEC, and only when
// no recovery poll has taken ownership (setRecoveryActive).
const PROGRESS_PHASE1_SEC = 50;   // end of fast segment
const PROGRESS_PHASE1_PCT = 75;   // pct reached at end of fast segment
const PROGRESS_PHASE2_SEC = 90;   // end of slow segment
const PROGRESS_PHASE2_PCT = 99;   // pct reached at end of slow segment (then parks)
const GENERATION_DEADLINE_SEC = 150; // 2m30s — hand off to fetch-gen recovery
const HARD_FAIL_SEC = 420;           // 7m — absolute backstop when nothing owns recovery

/** Two-segment progress curve, parking at 99%. Returns 0-99. */
function computeProgressPct(t) {
  if (t <= PROGRESS_PHASE1_SEC) {
    return (t / PROGRESS_PHASE1_SEC) * PROGRESS_PHASE1_PCT;
  }
  if (t <= PROGRESS_PHASE2_SEC) {
    var span = PROGRESS_PHASE2_SEC - PROGRESS_PHASE1_SEC;          // 40s
    var gain = PROGRESS_PHASE2_PCT - PROGRESS_PHASE1_PCT;          // 24%
    return PROGRESS_PHASE1_PCT + ((t - PROGRESS_PHASE1_SEC) / span) * gain;
  }
  return PROGRESS_PHASE2_PCT; // parked at 99%
}

let _interval = null;
let _startTime = 0;
let _deadlineFired = false;   // 'wolden:gen-deadline' is one-shot per ticker run
let _recoveryActive = false;  // true while wc77-upload's fetch-gen poll owns the outcome

/**
 * Recovery-ownership flag. While true: the hard-fail flip is suppressed (the
 * recovery poll in wc77-upload.js decides success/failure) and the visible
 * copy fast-forwards to the patient entries so the customer isn't stuck on
 * "Final polishes" while we quietly re-fetch their finished art.
 */
export function setRecoveryActive(on) { _recoveryActive = !!on; }

/** Start (or restart) the loading ticker. Sets modal to 'generating' state. */
export function start() {
  setModalState('generating');
  _startTime = performance.now();
  _deadlineFired = false;
  _recoveryActive = false;
  if (_interval) clearInterval(_interval);
  _interval = setInterval(tick, 1000);
  tick();                           // immediate first tick — no flash on initial paint
}

/** Stop the ticker. Caller is responsible for transitioning modal state. */
export function stop() {
  if (_interval) { clearInterval(_interval); _interval = null; }
}

/** Alias of start() for caller compatibility with the B-era API. */
export function resume() { start(); }

function tick() {
  const elapsedSec = (performance.now() - _startTime) / 1000;

  // Absolute backstop: only when no recovery poll owns the outcome. While
  // setRecoveryActive(true), wc77-upload.js decides (result or error) and
  // this flip is suppressed so we never error-over a poll that's about to
  // hydrate finished art.
  if (elapsedSec >= HARD_FAIL_SEC && !_recoveryActive) {
    stop();
    try { setModalState('error'); } catch (_) {}
    return;
  }

  // Soft deadline: hand the stalled generation to the fetch-gen recovery
  // poll (wc77-upload.js listens) instead of dead-ending. One-shot.
  if (elapsedSec >= GENERATION_DEADLINE_SEC && !_deadlineFired) {
    _deadlineFired = true;
    try {
      window.dispatchEvent(new CustomEvent('wolden:gen-deadline', { detail: { elapsedSec: elapsedSec } }));
    } catch (_) {}
  }

  // Find the latest LOADING_TIMELINE entry whose [0] <= copySec. While a
  // recovery poll is active the copy fast-forwards to the patient entries
  // even if recovery started before the 150s mark (e.g. an early network
  // error), so the customer always sees "still working" rather than a
  // mid-pipeline phase that no longer reflects reality.
  const copySec = _recoveryActive ? Math.max(elapsedSec, GENERATION_DEADLINE_SEC) : elapsedSec;
  let entry = LOADING_TIMELINE[0];
  for (let i = 0; i < LOADING_TIMELINE.length; i++) {
    if (LOADING_TIMELINE[i][0] <= copySec) entry = LOADING_TIMELINE[i];
    else break;
  }

  const titleEl = qs('[data-wc-element="loading-title"]');
  const subEl   = qs('[data-wc-element="loading-subtitle"]');
  const fillEl  = qs('[data-wc-element="progress-fill"]');

  if (titleEl && titleEl.textContent !== entry[1]) titleEl.textContent = entry[1];
  if (subEl   && subEl.textContent   !== entry[2]) subEl.textContent   = entry[2];

  if (fillEl) {
    const pct = computeProgressPct(elapsedSec);
    fillEl.style.width = pct.toFixed(2) + '%';
  }
}
